import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SourceReference } from '../services/api';
import { SourceCard } from './SourceCard';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: SourceReference[];
}

interface Props {
  message: Message;
}

export const MessageBubble: React.FC<Props> = ({ message }) => {
  const isUser = message.role === 'user';

  // Replace [N] citation markers with clickable markdown links to source URLs
  const processedContent = useMemo(() => {
    if (isUser || !message.sources || message.sources.length === 0) return message.content;
    const urlMap: Record<number, string> = {};
    message.sources.forEach((s, i) => { urlMap[s.citationIndex || i + 1] = s.url; });
    return message.content.replace(/\[(\d+)\]/g, (match, num) => {
      const url = urlMap[parseInt(num, 10)];
      return url ? `[[${num}]](${url})` : match;
    });
  }, [message.content, message.sources, isUser]);

  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
      <div
        style={{
          maxWidth: '75%',
          padding: '12px 16px',
          borderRadius: 16,
          background: isUser ? 'var(--bg-bubble-user)' : 'var(--bg-bubble-assistant)',
          color: isUser ? '#fff' : 'var(--text-primary)',
          borderBottomRightRadius: isUser ? 4 : 16,
          borderBottomLeftRadius: isUser ? 16 : 4,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {isUser ? (
          <span style={{ whiteSpace: 'pre-wrap' }}>{message.content}</span>
        ) : (
          <div className="md-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {processedContent}
            </ReactMarkdown>
          </div>
        )}
        {message.sources && message.sources.length > 0 && (
          <div style={{ marginTop: 8, borderTop: '1px solid var(--border-color)', paddingTop: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Zdroje:</div>
            {message.sources.map((s, i) => (
              <SourceCard key={i} source={s} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
